import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Printer, Package, Tags, Gift, TrendingUp, Building, Users, ArrowRight } from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <Printer className="w-10 h-10" />,
      title: 'Commercial Printing',
      description: 'Full-service printing operations supporting offset, digital, and specialty commercial print production.',
      image: 'https://images.unsplash.com/photo-1503694978374-8a2fa686963a'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Large Format Printing',
      description: 'Wide format print operations including banners, posters, vehicle wraps, and outdoor advertising.',
      image: 'https://images.unsplash.com/photo-1693031630369-bd429a57f115'
    },
    {
      icon: <Tags className="w-10 h-10" />,
      title: 'Signage Companies',
      description: 'Indoor and outdoor signage production, custom sign manufacturing, and installation coordination.',
      image: 'https://images.unsplash.com/photo-1773525912476-213bff96b8a4'
    },
    {
      icon: <Package className="w-10 h-10" />,
      title: 'Packaging Printing',
      description: 'Custom packaging solutions, label printing, and product packaging manufacturing.',
      image: 'https://images.unsplash.com/photo-1597765654576-f23f48b956ad'
    },
    {
      icon: <Gift className="w-10 h-10" />,
      title: 'Promotional Products',
      description: 'Custom branded merchandise, promotional items, and corporate gift production services.',
      image: 'https://images.pexels.com/photos/7709276/pexels-photo-7709276.jpeg'
    },
    {
      icon: <Building className="w-10 h-10" />,
      title: 'Graphic Production Facilities',
      description: 'Multi-service production facilities offering design, pre-press, and printing services.',
      image: 'https://images.pexels.com/photos/8875503/pexels-photo-8875503.jpeg'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Print Brokers',
      description: 'Print brokerage operations coordinating multiple vendors and managing client relationships.',
      image: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Marketing Production Agencies',
      description: 'Agencies managing marketing collateral production, fulfillment, and distribution.',
      image: 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a'
    }
  ];

  return (
    <div className="industries-page">
      {/* Hero Section */}
      <section className="page-hero py-24 bg-gradient-to-br from-cyan-50 to-green-50">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Built for <span className="text-gradient">Print Businesses</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto">
            Specialized remote staffing solutions tailored to your industry's unique needs
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep expertise across all segments of the printing and production industry
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <Card
                key={index}
                className="industry-card group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={industry.image}
                    alt={industry.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center text-cyan-600">
                      {industry.icon}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3">{industry.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{industry.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Building Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Serving Print Businesses Across North America</h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-12">
              From small print shops to large commercial operations, we help businesses build reliable remote teams that integrate seamlessly into their production workflows.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-gradient mb-3">Technical Expertise</h3>
                <p className="text-gray-700">Professionals trained in industry-standard software and workflows</p>
              </Card>
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-gradient mb-3">Operational Reliability</h3>
                <p className="text-gray-700">Dedicated remote staff aligned with your business needs</p>
              </Card>
              <Card className="p-8 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-gradient mb-3">Industry Focused</h3>
                <p className="text-gray-700">Specialized exclusively in printing and production environments</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-r from-cyan-500 to-green-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Scale Your Operations?</h2>
          <p className="text-xl lg:text-2xl mb-10 opacity-90 max-w-3xl mx-auto">
            Discover how specialized remote talent can transform your print business.
          </p>
          <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-10 py-6 font-semibold">
            Schedule Your Consultation
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Industries;